import { useState, useMemo, Fragment } from "react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt, ResultCard } from "./pipe-helpers";
import { WEIGHT_PIPE_DATA, WATER_DENSITY_LB_PER_CUIN } from "./pipe-weight-data";

/* ═══════════════════════════════════════════════════════
   SHARED DATA — Insulation, Jacket, Rod, Strut
   ═══════════════════════════════════════════════════════ */

const INSUL_DATA: { label: string; thk: number; lbPerFtByOD: (od: number) => number }[] = [
  { label: "1\" Fiberglass", thk: 1.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 1) ** 2 - (od / 2) ** 2) / 144 * 3.0 * 12 },
  { label: "1.5\" Fiberglass", thk: 1.5, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 1.5) ** 2 - (od / 2) ** 2) / 144 * 3.0 * 12 },
  { label: "2\" Fiberglass", thk: 2.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 2) ** 2 - (od / 2) ** 2) / 144 * 3.0 * 12 },
  { label: "3\" Fiberglass", thk: 3.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 3) ** 2 - (od / 2) ** 2) / 144 * 3.0 * 12 },
  { label: "1\" Calcium Silicate", thk: 1.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 1) ** 2 - (od / 2) ** 2) / 144 * 15.0 * 12 },
  { label: "2\" Calcium Silicate", thk: 2.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 2) ** 2 - (od / 2) ** 2) / 144 * 15.0 * 12 },
  { label: "1\" Elastomeric Foam", thk: 1.0, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 1) ** 2 - (od / 2) ** 2) / 144 * 4.5 * 12 },
  { label: "1.5\" Elastomeric Foam", thk: 1.5, lbPerFtByOD: (od) => Math.PI * ((od / 2 + 1.5) ** 2 - (od / 2) ** 2) / 144 * 4.5 * 12 },
  { label: "None", thk: 0, lbPerFtByOD: () => 0 },
];

const JACKET_DATA = [
  { label: "None", lbPerFtByOD: (_od: number, _insulThk: number) => 0 },
  { label: "Aluminum (.016\")", lbPerFtByOD: (od: number, insulThk: number) => Math.PI * (od + insulThk * 2) / 12 * 0.016 / 12 * 170 * 12 },
  { label: "PVC (.030\")", lbPerFtByOD: (od: number, insulThk: number) => Math.PI * (od + insulThk * 2) / 12 * 0.030 / 12 * 87 * 12 },
];

const ROD_SIZES: { dia: string; diaIn: number; rootArea: number; capacity: number }[] = [
  { dia: "3/8\"",  diaIn: 0.375, rootArea: 0.0775, capacity: 610 },
  { dia: "1/2\"",  diaIn: 0.500, rootArea: 0.1419, capacity: 1130 },
  { dia: "5/8\"",  diaIn: 0.625, rootArea: 0.2260, capacity: 1810 },
  { dia: "3/4\"",  diaIn: 0.750, rootArea: 0.3340, capacity: 2670 },
  { dia: "7/8\"",  diaIn: 0.875, rootArea: 0.4617, capacity: 3690 },
  { dia: "1\"",    diaIn: 1.000, rootArea: 0.6057, capacity: 4850 },
  { dia: "1-1/8\"",diaIn: 1.125, rootArea: 0.7627, capacity: 6100 },
  { dia: "1-1/4\"",diaIn: 1.250, rootArea: 0.9691, capacity: 7750 },
  { dia: "1-3/8\"",diaIn: 1.375, rootArea: 1.1549, capacity: 9240 },
  { dia: "1-1/2\"",diaIn: 1.500, rootArea: 1.4053, capacity: 11240 },
  { dia: "1-3/4\"",diaIn: 1.750, rootArea: 1.8992, capacity: 15190 },
  { dia: "2\"",    diaIn: 2.000, rootArea: 2.4981, capacity: 19980 },
];

const STRUT_SIZES: {
  name: string; profile: string; gauge: string;
  loads: { spanIn: number; capacity: number }[];
}[] = [
  { name: "P1000 (1-5/8×1-5/8) 12ga", profile: "1-5/8×1-5/8", gauge: "12 ga",
    loads: [{ spanIn: 24, capacity: 1350 },{ spanIn: 36, capacity: 900 },{ spanIn: 48, capacity: 675 },{ spanIn: 60, capacity: 540 },{ spanIn: 72, capacity: 450 },{ spanIn: 84, capacity: 385 },{ spanIn: 96, capacity: 340 },{ spanIn: 120, capacity: 270 }] },
  { name: "P1001 (1-5/8×1-5/8) 14ga", profile: "1-5/8×1-5/8", gauge: "14 ga",
    loads: [{ spanIn: 24, capacity: 950 },{ spanIn: 36, capacity: 635 },{ spanIn: 48, capacity: 475 },{ spanIn: 60, capacity: 380 },{ spanIn: 72, capacity: 320 },{ spanIn: 84, capacity: 270 },{ spanIn: 96, capacity: 240 },{ spanIn: 120, capacity: 190 }] },
  { name: "P5500 (1-5/8×3-1/4) Deep 12ga", profile: "1-5/8×3-1/4", gauge: "12 ga",
    loads: [{ spanIn: 24, capacity: 2700 },{ spanIn: 36, capacity: 1800 },{ spanIn: 48, capacity: 1350 },{ spanIn: 60, capacity: 1080 },{ spanIn: 72, capacity: 900 },{ spanIn: 84, capacity: 770 },{ spanIn: 96, capacity: 675 },{ spanIn: 120, capacity: 540 }] },
  { name: "Back-to-Back P1000", profile: "2×1-5/8×1-5/8", gauge: "12 ga",
    loads: [{ spanIn: 24, capacity: 2700 },{ spanIn: 36, capacity: 1800 },{ spanIn: 48, capacity: 1350 },{ spanIn: 60, capacity: 1080 },{ spanIn: 72, capacity: 900 },{ spanIn: 84, capacity: 770 },{ spanIn: 96, capacity: 675 },{ spanIn: 120, capacity: 540 }] },
];

const interpolateStrutCapacity = (loads: { spanIn: number; capacity: number }[], spanIn: number): number => {
  if (spanIn <= loads[0].spanIn) return loads[0].capacity;
  if (spanIn >= loads[loads.length - 1].spanIn) return loads[loads.length - 1].capacity;
  for (let i = 0; i < loads.length - 1; i++) {
    if (spanIn >= loads[i].spanIn && spanIn <= loads[i + 1].spanIn) {
      const frac = (spanIn - loads[i].spanIn) / (loads[i + 1].spanIn - loads[i].spanIn);
      return loads[i].capacity - frac * (loads[i].capacity - loads[i + 1].capacity);
    }
  }
  return loads[loads.length - 1].capacity;
};

/* ═══════════════════════════════════════════════════════
   ROD SIZING RECOMMENDATION
   ═══════════════════════════════════════════════════════ */

const RodSizingRecommendation = ({ designLoad }: { designLoad: number }) => {
  const recommended = ROD_SIZES.find(r => r.capacity >= designLoad);
  const nextUp = recommended ? ROD_SIZES[ROD_SIZES.indexOf(recommended) + 1] : undefined;

  return (
    <div className="trade-card">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Rod Sizing Recommendation</h4>
      {recommended ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">Min. Rod: {recommended.dia}</span>
            <span className="text-xs font-mono-data text-primary font-bold">{recommended.capacity} lb capacity</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2">
            <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${Math.min(100, (designLoad / recommended.capacity) * 100)}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{fmt(designLoad, 0)} lb load</span>
            <span>{fmt((designLoad / recommended.capacity) * 100, 0)}% utilized</span>
          </div>
          {nextUp && (
            <p className="text-[10px] text-muted-foreground">
              Next size up: {nextUp.dia} ({nextUp.capacity} lb)
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-destructive font-semibold">
          Load exceeds {ROD_SIZES[ROD_SIZES.length - 1].capacity} lb — requires engineered support
        </p>
      )}

      <details className="mt-3">
        <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
          Full rod capacity table
        </summary>
        <div className="mt-2 grid grid-cols-3 gap-y-1 text-[10px]">
          <span className="text-muted-foreground font-semibold">Rod</span>
          <span className="text-muted-foreground font-semibold text-right">Root Area</span>
          <span className="text-muted-foreground font-semibold text-right">Capacity</span>
          {ROD_SIZES.map(r => (
            <Fragment key={r.dia}>
              <span className={`font-mono-data ${recommended && r.dia === recommended.dia ? "text-primary font-bold" : ""}`}>{r.dia}</span>
              <span className="font-mono-data text-right">{r.rootArea} in²</span>
              <span className={`font-mono-data text-right ${recommended && r.dia === recommended.dia ? "text-primary font-bold" : ""}`}>{r.capacity} lb</span>
            </Fragment>
          ))}
        </div>
      </details>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   STRUT / CHANNEL SIZING RECOMMENDATION
   ═══════════════════════════════════════════════════════ */

const StrutSizingRecommendation = ({ designLoad, spanIn }: { designLoad: number; spanIn: number }) => {
  const candidates = STRUT_SIZES.map(strut => {
    const capacity = interpolateStrutCapacity(strut.loads, spanIn);
    const utilization = (designLoad / capacity) * 100;
    return { ...strut, capacity, utilization, passes: utilization <= 100 };
  });
  const recommended = candidates.find(c => c.passes);

  return (
    <div className="trade-card">
      <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Strut / Channel Sizing</h4>
      {recommended ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-foreground">{recommended.name}</span>
            <span className="text-xs font-mono-data text-primary font-bold">{fmt(recommended.capacity, 0)} lb</span>
          </div>
          <div className="w-full bg-secondary rounded-full h-2">
            <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${Math.min(100, recommended.utilization)}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>{fmt(designLoad, 0)} lb @ {fmt(spanIn, 0)}" span</span>
            <span>{fmt(recommended.utilization, 0)}% utilized</span>
          </div>
        </div>
      ) : (
        <p className="text-sm text-destructive font-semibold">Load exceeds standard strut capacity — requires engineered beam</p>
      )}
      <details className="mt-3">
        <summary className="text-[10px] text-muted-foreground cursor-pointer hover:text-foreground transition-colors">
          All strut options at {fmt(spanIn, 0)}" span
        </summary>
        <div className="mt-2 space-y-1.5">
          {candidates.map((c, i) => (
            <div key={i} className={`flex items-center justify-between text-[11px] px-2 py-1 rounded ${c.passes ? "bg-secondary/50" : "bg-destructive/10"}`}>
              <span className={`truncate mr-2 ${recommended && c.name === recommended.name ? "text-primary font-bold" : c.passes ? "text-foreground" : "text-destructive"}`}>{c.name}</span>
              <span className="font-mono-data whitespace-nowrap">{fmt(c.capacity, 0)} lb · {fmt(c.utilization, 0)}%</span>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   HANGER LOAD CALCULATOR
   ═══════════════════════════════════════════════════════ */

export const HangerLoadCalculator = () => {
  const [matIdx, setMatIdx] = useState("0");
  const [sizeIdx, setSizeIdx] = useState("5");
  const [span, setSpan] = useState("10");
  const [fillType, setFillType] = useState<"water" | "empty" | "glycol" | "steam">("water");
  const [insulIdx, setInsulIdx] = useState(String(INSUL_DATA.length - 1));
  const [jacketIdx, setJacketIdx] = useState("0");
  const [valveWt, setValveWt] = useState("");
  const [safetyFactor, setSafetyFactor] = useState("1.5");

  const mat = WEIGHT_PIPE_DATA[parseInt(matIdx)];
  const pipe = mat?.sizes[parseInt(sizeIdx)];
  const insul = INSUL_DATA[parseInt(insulIdx)];
  const jacket = JACKET_DATA[parseInt(jacketIdx)];
  const spanFt = parseFloat(span);
  const valveLbs = parseFloat(valveWt) || 0;
  const sf = parseFloat(safetyFactor) || 1.5;

  const fillDensity = fillType === "water" ? WATER_DENSITY_LB_PER_CUIN
    : fillType === "glycol" ? WATER_DENSITY_LB_PER_CUIN * 1.04
    : fillType === "steam" ? WATER_DENSITY_LB_PER_CUIN * 0.003
    : 0;

  const result = useMemo(() => {
    if (!pipe || !spanFt || spanFt <= 0) return null;

    const pipeWtPerFt = pipe.wt;
    const idArea = Math.PI * (pipe.id / 2) ** 2;
    const fillWtPerFt = idArea * 12 * fillDensity;
    const insulWtPerFt = insul.lbPerFtByOD(pipe.od);
    const jacketWtPerFt = jacket.lbPerFtByOD(pipe.od, insul.thk);

    const totalWtPerFt = pipeWtPerFt + fillWtPerFt + insulWtPerFt + jacketWtPerFt;
    const spanLoad = totalWtPerFt * spanFt + valveLbs;
    const designLoad = spanLoad * sf;

    return { pipeWtPerFt, fillWtPerFt, insulWtPerFt, jacketWtPerFt, totalWtPerFt, spanLoad, designLoad, valveLbs };
  }, [pipe, spanFt, fillDensity, insul, jacket, valveLbs, sf]);

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">{children}</label>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Material</FieldLabel>
          <Select value={matIdx} onValueChange={(v) => { setMatIdx(v); setSizeIdx("0"); }}>
            <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {WEIGHT_PIPE_DATA.map((m, i) => (
                <SelectItem key={i} value={String(i)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Pipe Size</FieldLabel>
          <Select value={sizeIdx} onValueChange={setSizeIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              {mat?.sizes.map((s, i) => (
                <SelectItem key={i} value={String(i)}>{s.nom}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Support Span (ft)</FieldLabel>
          <Input type="number" inputMode="decimal" placeholder="10" value={span} onChange={(e) => setSpan(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <FieldLabel>Fill</FieldLabel>
          <Select value={fillType} onValueChange={(v) => setFillType(v as typeof fillType)}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="water">Water</SelectItem>
              <SelectItem value="glycol">50% Glycol</SelectItem>
              <SelectItem value="steam">Steam</SelectItem>
              <SelectItem value="empty">Empty / Gas</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <FieldLabel>Insulation</FieldLabel>
        <Select value={insulIdx} onValueChange={setInsulIdx}>
          <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {INSUL_DATA.map((ins, i) => (
              <SelectItem key={i} value={String(i)}>{ins.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Jacket / Cladding</FieldLabel>
          <Select value={jacketIdx} onValueChange={setJacketIdx}>
            <SelectTrigger className="bg-card border-border font-mono-data text-xs sm:text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              {JACKET_DATA.map((j, i) => (
                <SelectItem key={i} value={String(i)}>{j.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel>Safety Factor</FieldLabel>
          <Select value={safetyFactor} onValueChange={setSafetyFactor}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1.0">1.0×</SelectItem>
              <SelectItem value="1.25">1.25×</SelectItem>
              <SelectItem value="1.5">1.5×</SelectItem>
              <SelectItem value="2.0">2.0×</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <FieldLabel>Point Load — Valve / Fitting (lbs)</FieldLabel>
        <Input type="number" inputMode="decimal" placeholder="Optional — added at hanger" value={valveWt} onChange={(e) => setValveWt(e.target.value)} className="font-mono-data bg-card border-border" />
      </div>

      {result && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Design Hanger Load" value={fmt(result.designLoad, 0)} unit="lbs" accent />
          <ResultCard label="Working Load" value={fmt(result.spanLoad, 0)} unit="lbs" />

          <div className="trade-card">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Weight Breakdown (per ft)</h4>
            <div className="grid grid-cols-2 gap-y-1.5 text-xs">
              <span className="text-muted-foreground">Pipe</span>
              <span className="font-mono-data text-right">{fmt(result.pipeWtPerFt, 2)} lb/ft</span>
              <span className="text-muted-foreground">Fill ({fillType})</span>
              <span className="font-mono-data text-right">{fmt(result.fillWtPerFt, 2)} lb/ft</span>
              <span className="text-muted-foreground">Insulation</span>
              <span className="font-mono-data text-right">{fmt(result.insulWtPerFt, 2)} lb/ft</span>
              <span className="text-muted-foreground">Jacket</span>
              <span className="font-mono-data text-right">{fmt(result.jacketWtPerFt, 2)} lb/ft</span>
              <span className="text-muted-foreground font-semibold border-t border-border pt-1">Total</span>
              <span className="font-mono-data text-right font-semibold border-t border-border pt-1">{fmt(result.totalWtPerFt, 2)} lb/ft</span>
            </div>
            {result.valveLbs > 0 && (
              <div className="grid grid-cols-2 gap-y-1 text-xs mt-1.5 pt-1.5 border-t border-border">
                <span className="text-muted-foreground">Valve / Fitting</span>
                <span className="font-mono-data text-right">+{fmt(result.valveLbs, 0)} lbs</span>
              </div>
            )}
          </div>

          <div className="trade-card">
            <div className="grid grid-cols-2 gap-y-1 text-xs">
              <span className="text-muted-foreground">Span</span>
              <span className="font-mono-data text-right">{span} ft</span>
              <span className="text-muted-foreground">Safety Factor</span>
              <span className="font-mono-data text-right">{safetyFactor}×</span>
            </div>
          </div>

          <RodSizingRecommendation designLoad={result.designLoad} />

          <p className="text-[10px] text-muted-foreground leading-snug">
            Rod capacities based on ASTM A36 carbon steel, root area at UNC threads. For seismic zones, apply additional bracing per ASCE 7 / IBC requirements.
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   TRAPEZE HANGER CALCULATOR
   ═══════════════════════════════════════════════════════ */

interface TrapezePipe {
  id: string;
  matIdx: string;
  sizeIdx: string;
  fillType: "water" | "empty" | "glycol" | "steam";
  insulIdx: string;
}

let _trapezeId = 0;
const newTrapezePipe = (): TrapezePipe => ({
  id: String(++_trapezeId),
  matIdx: "0",
  sizeIdx: "5",
  fillType: "water",
  insulIdx: String(INSUL_DATA.length - 1),
});

export const TrapezeHangerCalculator = () => {
  const [pipes, setPipes] = useState<TrapezePipe[]>([newTrapezePipe(), newTrapezePipe()]);
  const [span, setSpan] = useState("10");
  const [extraLoad, setExtraLoad] = useState("");
  const [safetyFactor, setSafetyFactor] = useState("1.5");

  const spanFt = parseFloat(span);
  const extraLbs = parseFloat(extraLoad) || 0;
  const sf = parseFloat(safetyFactor) || 1.5;

  const updatePipe = (id: string, patch: Partial<TrapezePipe>) => {
    setPipes(prev => prev.map(p => p.id === id ? { ...p, ...patch } : p));
  };

  const addPipe = () => setPipes(prev => [...prev, newTrapezePipe()]);
  const removePipe = (id: string) => setPipes(prev => prev.length > 1 ? prev.filter(p => p.id !== id) : prev);

  const pipeResults = useMemo(() => {
    if (!spanFt || spanFt <= 0) return null;

    return pipes.map(p => {
      const mat = WEIGHT_PIPE_DATA[parseInt(p.matIdx)];
      const pipe = mat?.sizes[parseInt(p.sizeIdx)];
      const insul = INSUL_DATA[parseInt(p.insulIdx)];
      if (!pipe) return null;

      const fillDensity = p.fillType === "water" ? WATER_DENSITY_LB_PER_CUIN
        : p.fillType === "glycol" ? WATER_DENSITY_LB_PER_CUIN * 1.04
        : p.fillType === "steam" ? WATER_DENSITY_LB_PER_CUIN * 0.003
        : 0;

      const pipeWt = pipe.wt;
      const idArea = Math.PI * (pipe.id / 2) ** 2;
      const fillWt = idArea * 12 * fillDensity;
      const insulWt = insul.lbPerFtByOD(pipe.od);
      const totalPerFt = pipeWt + fillWt + insulWt;

      return {
        label: `${mat.label} ${pipe.nom}`,
        pipeWt,
        fillWt,
        insulWt,
        totalPerFt,
        spanLoad: totalPerFt * spanFt,
        od: pipe.od,
        insulThk: insul.thk,
        overallOD: pipe.od + insul.thk * 2,
      };
    }).filter((x): x is { label: string; pipeWt: number; fillWt: number; insulWt: number; totalPerFt: number; spanLoad: number; od: number; insulThk: number; overallOD: number } => x !== null);
  }, [pipes, spanFt]);

  const totals = useMemo(() => {
    if (!pipeResults || pipeResults.length === 0) return null;
    const totalPerFt = pipeResults.reduce((s, p) => s + p.totalPerFt, 0);
    const totalSpanLoad = pipeResults.reduce((s, p) => s + p.spanLoad, 0);
    const workingLoad = totalSpanLoad + extraLbs;
    const designLoad = workingLoad * sf;
    const totalWidth = pipeResults.reduce((s, p) => s + p.overallOD, 0) + (pipeResults.length - 1) * 1.5;
    return { totalPerFt, totalSpanLoad, workingLoad, designLoad, totalWidth };
  }, [pipeResults, extraLbs, sf]);

  const FieldLabel = ({ children }: { children: React.ReactNode }) => (
    <label className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-1 block">{children}</label>
  );

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-xl p-3">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Calculate <span className="text-foreground font-semibold">trapeze hanger loads</span> for multiple parallel pipes sharing a single support. Add pipes, set span, and get total design load with rod sizing.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <FieldLabel>Support Span (ft)</FieldLabel>
          <Input type="number" inputMode="decimal" placeholder="10" value={span} onChange={(e) => setSpan(e.target.value)} className="font-mono-data bg-card border-border" />
        </div>
        <div>
          <FieldLabel>Safety Factor</FieldLabel>
          <Select value={safetyFactor} onValueChange={setSafetyFactor}>
            <SelectTrigger className="bg-card border-border font-mono-data"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1.0">1.0×</SelectItem>
              <SelectItem value="1.25">1.25×</SelectItem>
              <SelectItem value="1.5">1.5×</SelectItem>
              <SelectItem value="2.0">2.0×</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <FieldLabel>Pipes on Trapeze</FieldLabel>
          <button onClick={addPipe} className="text-xs text-primary font-semibold hover:text-primary/80 transition-colors">+ Add Pipe</button>
        </div>

        {pipes.map((p, idx) => {
          const mat = WEIGHT_PIPE_DATA[parseInt(p.matIdx)];
          return (
            <div key={p.id} className="trade-card relative">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Pipe {idx + 1}</span>
                {pipes.length > 1 && (
                  <button onClick={() => removePipe(p.id)} className="text-[10px] text-destructive hover:text-destructive/80 font-semibold transition-colors">Remove</button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Select value={p.matIdx} onValueChange={(v) => updatePipe(p.id, { matIdx: v, sizeIdx: "0" })}>
                  <SelectTrigger className="bg-background border-border font-mono-data text-[11px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {WEIGHT_PIPE_DATA.map((m, i) => (
                      <SelectItem key={i} value={String(i)}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={p.sizeIdx} onValueChange={(v) => updatePipe(p.id, { sizeIdx: v })}>
                  <SelectTrigger className="bg-background border-border font-mono-data text-[11px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {mat?.sizes.map((s, i) => (
                      <SelectItem key={i} value={String(i)}>{s.nom}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={p.fillType} onValueChange={(v) => updatePipe(p.id, { fillType: v as TrapezePipe["fillType"] })}>
                  <SelectTrigger className="bg-background border-border font-mono-data text-[11px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="water">Water</SelectItem>
                    <SelectItem value="glycol">50% Glycol</SelectItem>
                    <SelectItem value="steam">Steam</SelectItem>
                    <SelectItem value="empty">Empty</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={p.insulIdx} onValueChange={(v) => updatePipe(p.id, { insulIdx: v })}>
                  <SelectTrigger className="bg-background border-border font-mono-data text-[11px] h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {INSUL_DATA.map((ins, i) => (
                      <SelectItem key={i} value={String(i)} className="text-xs">{ins.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          );
        })}
      </div>

      <div>
        <FieldLabel>Extra Point Load (lbs)</FieldLabel>
        <Input type="number" inputMode="decimal" placeholder="Valves, fittings…" value={extraLoad} onChange={(e) => setExtraLoad(e.target.value)} className="font-mono-data bg-card border-border" />
      </div>

      {totals && pipeResults && pipeResults.length > 0 && (
        <div className="space-y-2 animate-fade-in">
          <ResultCard label="Design Load (per hanger)" value={fmt(totals.designLoad, 0)} unit="lbs" accent />
          <ResultCard label="Working Load" value={fmt(totals.workingLoad, 0)} unit="lbs" />
          <ResultCard label="Combined Weight" value={fmt(totals.totalPerFt, 2)} unit="lb/ft" />
          <ResultCard label="Min. Trapeze Width" value={fmt(totals.totalWidth, 1)} unit="in" />

          <div className="trade-card">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Per-Pipe Breakdown</h4>
            <div className="space-y-2">
              {pipeResults.map((pr, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground truncate mr-2">{pr.label}</span>
                  <span className="font-mono-data whitespace-nowrap">{fmt(pr.totalPerFt, 2)} lb/ft · {fmt(pr.spanLoad, 0)} lb</span>
                </div>
              ))}
              {extraLbs > 0 && (
                <div className="flex items-center justify-between text-xs border-t border-border pt-1">
                  <span className="text-muted-foreground">Extra point load</span>
                  <span className="font-mono-data">+{fmt(extraLbs, 0)} lbs</span>
                </div>
              )}
            </div>
          </div>

          <div className="trade-card">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Rod Sizing (each side)</h4>
            {(() => {
              const perRod = totals.designLoad / 2;
              const recommended = ROD_SIZES.find(r => r.capacity >= perRod);
              if (!recommended) return <p className="text-sm text-destructive font-semibold">Load exceeds max rod capacity — requires engineered support</p>;
              const util = (perRod / recommended.capacity) * 100;
              return (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-foreground">Min. Rod: {recommended.dia} (×2)</span>
                    <span className="text-xs font-mono-data text-primary font-bold">{recommended.capacity} lb ea.</span>
                  </div>
                  <div className="w-full bg-secondary rounded-full h-2">
                    <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${Math.min(100, util)}%` }} />
                  </div>
                  <div className="flex justify-between text-[10px] text-muted-foreground">
                    <span>{fmt(perRod, 0)} lb per rod</span>
                    <span>{fmt(util, 0)}% utilized</span>
                  </div>
                </div>
              );
            })()}
          </div>

          <div className="trade-card">
            <h4 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2">Cross Section</h4>
            <div className="flex justify-center">
              <svg width="100%" viewBox={`0 0 ${Math.max(200, totals.totalWidth * 3 + 40)} 80`} className="max-w-sm">
                <rect x="5" y="5" width={Math.max(190, totals.totalWidth * 3 + 30)} height="8" rx="1" className="fill-muted-foreground/30" />
                <rect x="10" y="5" width="3" height="75" className="fill-muted-foreground/50" />
                <rect x={Math.max(187, totals.totalWidth * 3 + 27)} y="5" width="3" height="75" className="fill-muted-foreground/50" />
                {pipeResults.map((pr, i) => {
                  const prevWidth = pipeResults.slice(0, i).reduce((s, p) => s + p.overallOD * 3 + 4.5, 0);
                  const cx = 20 + prevWidth + (pr.overallOD * 3) / 2;
                  const pipeR = (pr.od * 3) / 2;
                  const outerR = (pr.overallOD * 3) / 2;
                  return (
                    <g key={i}>
                      {pr.insulThk > 0 && <circle cx={cx} cy="50" r={outerR} className="fill-accent/40 stroke-accent" strokeWidth="0.5" />}
                      <circle cx={cx} cy="50" r={pipeR} className="fill-primary/20 stroke-primary" strokeWidth="1" />
                      <text x={cx} y="53" textAnchor="middle" className="fill-foreground text-[7px] font-semibold">{pr.label.split(" ").pop()}</text>
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <StrutSizingRecommendation designLoad={totals.designLoad} spanIn={totals.totalWidth + 6} />

          <p className="text-[10px] text-muted-foreground leading-snug">
            Trapeze designed with 1.5" spacing between pipes. Rod load split equally between two rods. Strut capacities are for simple beam, uniformly loaded, based on manufacturer published data.
          </p>
        </div>
      )}
    </div>
  );
};
